// TeamRole is used by the integration-definition / scope / HITL types folded in
// from the former types/plugin.ts (ENG-7168).
import type { TeamRole } from './team.js';

export type IntegrationScope = 'organization' | 'team' | 'agent';

// `pending_approval` (ENG-9206): a connect flow was STARTED against a tenant
// that requires admin consent, and terminated at the approval screen without
// issuing a code. Recorded intent, never a probe result — Microsoft's
// admin-consent path does not call back, so "waiting on an admin" is
// indistinguishable from "closed the tab" from the outside.
//
// It sits deliberately OUTSIDE ('active','configured') — the set both
// `POST /host/agent-integrations` and `effective-integrations.ts` provision
// from — so it cannot bind tools and cannot count toward `effective_count` by
// construction rather than by a filter someone has to remember to add. See
// `integrations/pending-approval.ts`.
export type IntegrationStatus =
  | 'pending'
  | 'configured'
  | 'active'
  | 'error'
  | 'revoked'
  | 'pending_approval';

// `github_app` (CS-1441): BYO GitHub App installation auth. The install stores
// the App id + installation id (in `config`) and the App private key (in
// `credentials`); the runtime mints a short-lived installation token on demand
// rather than holding a long-lived user token. Additive alongside GitHub's
// existing oauth2 / api_key options.
//
// `host_oauth` (ENG-8396): no credential for US to store, but a REQUIRED browser
// OAuth brokered by the MCP host (Claude Code) on the agent's own machine. It is
// deliberately distinct from `none`: both are "nothing to paste", but `none`
// means Augmented Team owns the credential and the install is live immediately,
// whereas a `host_oauth` install is inert until an operator authenticates from
// the agent's session. Collapsing the two is what made Vercel MCP report itself
// as fully set up while every tool call failed. Classify via
// `isHostBrokeredOAuth()` in `integrations/keyless-auth.ts`, never an inline
// string compare — `toolkit_definitions.auth_types` is an unconstrained text[].
export type IntegrationAuthType =
  | 'oauth2'
  | 'api_key'
  | 'webhook'
  | 'managed'
  | 'none'
  | 'host_oauth'
  | 'github_app';

// ENG-8304 / ADR-0055: WHO owns the credential this install authenticates with.
// `managed` = Augmented's shared credential (platform OAuth app or shared API
// key) — the only case that exists pre-ENG-8304, so it is the storage default.
// `byo` = the customer's own credential (their API key, later their own OAuth
// app); its upstream cost is on the customer's vendor bill, so a BYO install is
// never metered. This is INDEPENDENT of {@link IntegrationAuthType}: a managed
// and a BYO api_key install share `auth_type: 'api_key'` and differ only here,
// so credential_source must never be derived from auth_type (and vice versa).
export type CredentialSource = 'managed' | 'byo';

export type IntegrationId = 'linear' | 'github' | 'google-workspace' | 'gcloud' | 'xero' | 'granola' | 'brand-ninja' | 'kajabi' | 'postiz' | 'higgsfield' | 'qmd' | 'v0' | 'vercel' | 'pika' | 'claude-code' | 'xurl' | 'coderabbit' | 'aws' | 'anchor-browser' | 'deck' | 'browserbase' | 'elevenlabs' | 'image-gen' | 'video-gen' | 'firecrawl' | 'origami' | 'x-search' | 'ayrshare' | 'social-scraping' | 'local-business-data' | 'augmented-admin' | 'augmented-support' | 'augmented-help-kb' | 'greenlight' | 'expo' | 'grok-voice' | 'ninjafy-notes' | 'lovable' | 'framer' | 'open-computer-use' | 'typed-evaluation' | 'custom';

export type IntegrationCategory =
  | 'project-management'
  | 'code'
  | 'accounting'
  | 'crm'
  | 'communication'
  | 'storage'
  | 'workspace-productivity'
  | 'knowledge'
  | 'ui-generation'
  | 'media'
  | 'social'
  | 'infrastructure'
  | 'custom';

export interface IntegrationCapability {
  id: string;
  name: string;
  description: string;
  access: 'read' | 'write' | 'admin';
  required_scopes?: string[];
  /**
   * ADR-0073: this capability is a CUSTOMER OPT-IN — off until the organization
   * turns it on, at Organization -> Settings -> Integrations -> Premium &
   * billing, and recorded in `org_integration_feature_opt_ins`.
   *
   * WHAT THIS IS NOT. It is not entitlement ("may this org have it", ADR-0039,
   * a plan column) and it is not a rollout gate ("is it safe for us to serve",
   * ADR-0022, `FLAG_REGISTRY`). It is the third question — "does this org WANT
   * it" — which the platform previously had nowhere to put, so it was answered
   * with a staff-controlled flag. `live-meeting-advisor` is the measured case:
   * on 2026-08-24 the only org using it lost the capability, and the remedy was
   * Integrity Labs staff flipping a flag by hand.
   *
   * WHY THE DECLARATION LIVES HERE, not in `integration_definitions`. The
   * scope ids themselves are seeded (`defined_scopes[].id`), but WHICH of them
   * a customer must opt in to is a gate, and a gate must not depend on catalog
   * seeding having run. That is verbatim the reasoning
   * `org_integration_policies`'s own migration gives for keeping `premium` in
   * this registry ("a policy must not depend on catalog seeding", ENG-6981),
   * and it applies here for the same reason: a seed that has not been applied
   * would make the gate silently absent, which is indistinguishable from a
   * capability that was never gated.
   *
   * Absent/false => not an opt-in capability, and the resolver never asks about
   * it. Only capabilities that are genuinely the customer's decision — a
   * metered exposure, a new class of data leaving their control — should carry
   * it; every scope becoming a checkbox is how a consent surface stops being
   * read.
   */
  requires_org_opt_in?: boolean;
}

export interface IntegrationCliTool {
  package: string;
  /** Binary name on PATH (e.g. 'linear', 'gh') — used by `command -v` to decide whether to install. */
  binary: string;
  env_key: string;
  skill_id?: string;
  /** Additional env vars to set alongside the API key */
  extra_env?: Record<string, string>;
  /**
   * How the manager should install the CLI when it's missing from the host.
   *   - 'npm': global install via `npm install -g <package>`
   *   - 'brew': install via `brew install <package>` (macOS, and Linuxbrew when present)
   *   - 'script': run `script` verbatim (whitelisted URL; the catalog is the trust boundary)
   *   - 'manual': do not auto-install — log a hint; operator handles it out of band.
   * Omit to default to 'manual' for backward compatibility.
   */
  installer?: 'npm' | 'brew' | 'script' | 'manual';
  /** Only used when installer === 'script'. Must be a single shell command. */
  script?: string;
}

/**
 * ENG-5855: declarative spec for a custom-header (non-OAuth) HOSTED remote
 * MCP, declared on an `IntegrationDefinition.remoteMcp` field. The claudecode
 * adapter renders it into `.mcp.json` via `buildRemoteMcpEntry`.
 *
 * Unlike the OAuth path (fixed `Authorization: Bearer ${ID_ACCESS_TOKEN}`),
 * the header names and values are arbitrary — the manager substitutes each
 * `${VAR}` from `.env.integrations` at MCP-spawn time. For Anchor Browser:
 *   headers: {
 *     'anchor-api-key':   '${ANCHOR_BROWSER_API_KEY}',   // from api_key cred
 *     'anchor-session-id':'${ANCHOR_BROWSER_SESSION_ID}', // minted by ENG-5857
 *   }
 */
/**
 * ENG-6993 / ADR-0033: structured auth for a hosted remote MCP. The runtime
 * renders the credential into the request header — there is deliberately NO
 * free-form `${VAR}` string the operator/catalog can set.
 *
 * SECURITY (ADR-0033 C1 — closes the credential-exfiltration surface): the
 * env var is DERIVED by `buildRemoteMcpEntry` from the integration's OWN
 * `definition_id` + `credential_ref` (`<DEFINITION_ID>_<CREDENTIAL_REF>`, e.g.
 * `anchor-browser` + `api_key` → `ANCHOR_BROWSER_API_KEY`). Because the name is
 * derived from the integration's own id, a catalog row can never reference a
 * DIFFERENT integration's / customer's secret — the confused-deputy template
 * injection of the rejected `headers:{Authorization:"Bearer ${ANY_VAR}"}` shape
 * is impossible by construction.
 */
export interface RemoteMcpAuth {
  /**
   * How to render the credential:
   *   - 'bearer' → `Authorization: Bearer <token>`
   *   - 'header' → `<header_name>: <token>` (custom header, e.g. Anchor's
   *     `anchor-api-key`). `header_name` is required for this scheme.
   */
  scheme: 'bearer' | 'header';
  /** Required when `scheme === 'header'` — the custom header name. */
  header_name?: string;
  /**
   * The credential FIELD name on this integration (e.g. `api_key`). The env
   * var is derived as `<DEFINITION_ID>_<CREDENTIAL_REF>` — never a free string.
   */
  credential_ref: string;
}

export interface RemoteMcpSpec {
  /** Transport — defaults to 'http' (streamable HTTP) when omitted. */
  type?: 'http' | 'sse';
  /** The hosted MCP endpoint. */
  url: string;
  /**
   * ENG-6993 / ADR-0033: structured auth (preferred). When present,
   * `buildRemoteMcpEntry` renders the credential header from this — the env var
   * is derived from this integration's own id, so it cannot reference another
   * integration's secret. New hosted-remote-MCP integrations (monday.com, and
   * Anchor post-migration) use `auth`; the legacy free-form `headers` below is
   * retained only for non-credential / dynamic headers (e.g. Anchor's
   * minted `anchor-session-id`) and is being phased out for credential headers.
   */
  auth?: RemoteMcpAuth;
  /**
   * Headers sent with each request. `${VAR}` values are resolved at
   * spawn time from `.env.integrations` (credential-derived vars like
   * `<ID>_API_KEY`, or vars another ticket populates).
   *
   * NOTE (ADR-0033): for CREDENTIAL headers prefer `auth` above — it scopes the
   * env var to this integration. `headers` remains for non-secret / dynamically
   * minted headers (e.g. `anchor-session-id`). A `headers` entry whose value
   * references a `${VAR}` not derivable from THIS integration is a smell.
   */
  headers?: Record<string, string>;
  /**
   * Env vars to seed in `.env.integrations` with a default value when the
   * integration is present but nothing else has written them yet. Prevents
   * a referenced-but-unset `${VAR}` from shipping as a literal placeholder
   * (which would corrupt the header). Anchor seeds `ANCHOR_BROWSER_SESSION_ID`
   * to '' so stateless browsing works until ENG-5857 mints a real session.
   * A later writer (real credential / config / session mint) overrides it.
   */
  envDefaults?: Record<string, string>;
  /**
   * ENG-7748: route this remote MCP through the stdio remote-MCP proxy
   * (packages/mcp/remote-oauth-proxy) instead of a direct streamable-HTTP entry,
   * so its `${VAR}` headers are read LIVE from `.env.integrations` on every
   * request. Required when a header rotates on the session's timescale - Anchor's
   * minted `anchor-session-id` - because a direct-HTTP header is frozen at spawn
   * and can only change via a full agent respawn. Static-key remotes (monday,
   * peec) leave this unset and keep the cheaper direct-HTTP entry. The proxy
   * forwards the `auth` credential as its header (`auth.header_name`) plus each
   * `${VAR}` entry in `headers` as a live-read header.
   */
  liveHeaderRefresh?: boolean;
}

/**
 * ENG-6920: how a premium (billable) integration is priced. `monthly` = a flat
 * subscription; `usage` = metered per unit of consumption (e.g. Deck compute
 * time + agent runs). The actual amounts are NOT here — they live with the
 * billing mechanism (Stripe), which is deferred. This only declares the model
 * so the catalog, entitlement and metering slices can branch on it.
 *
 * `included` (ENG-8842) is the third case, and it exists because the second
 * "premium" axis turned out not to be about money at all. An integration can
 * need a deliberate per-org opt-in — an org admin consciously turning it on for
 * everyone — while costing the platform nothing per call, and `isPremiumDefinition`
 * is the only mechanism that arms that opt-in. Ninja Notes is the first: STT
 * runs on the user's Mac, no platform model call summarises, and a long meeting
 * is tens of KB in S3, so there is nothing worth metering. Reading and
 * summarising the note burns the AGENT's turn, which is already billed —
 * metering the note too would charge for the same work through two mechanisms.
 *
 * Without this value such an integration has to claim `monthly` or `usage`, and
 * the acknowledgement modal then tells an org admin they are about to be billed
 * a subscription with no amount shown. That is a false statement in a consent
 * dialog, which is the one place it is least acceptable — the whole point of
 * the modal is that the admin knows what they are agreeing to.
 *
 * An `included` premium declares no meters, and until ENG-9278 that was stated
 * here as "no rate card and no meters, by construction", with the expectation
 * that acquiring a real marginal cost would flip the methodology. Ninja Notes
 * then acquired one — live advising, which is a platform-side model call per
 * chunk for as long as it is armed — and flipping was REFUSED. The reason is
 * worth carrying, because the same shape will recur:
 *
 * The cost did not arrive at the INTEGRATION's grain. It arrived at one
 * CAPABILITY's, behind its own `requires_org_opt_in`, while the rest of the
 * product stayed genuinely free. Flipping the definition to `usage` would have
 * told every org that records notes and never arms an advisor that their
 * integration "bills by usage" — a false statement in a consent dialog, which is
 * precisely the harm this value was added to prevent, only inverted. So the
 * price lives on the capability's own description, where the consent for it
 * happens, and the definition stays `included`.
 *
 * So: an `included` premium may have a rate card for a metered CAPABILITY
 * beneath it. What it must not have is a metered path that every holder of the
 * integration is on — that is the case that still changes methodology. The
 * structurally cleaner answer, meters declared on the capability descriptor and
 * read by the premium surface, is the follow-up to ENG-9278.
 *
 * NOTE for anyone reaching for a precedent: `agt-live` is NOT one, despite what
 * ADR-0063 Decision 9 and ENG-8842 both say. It has no entry in the integration
 * registry at all, so `isPremiumDefinition('agt-live')` is false and it is a
 * free integration on this axis. Its premium tier is `plans.live_premium`
 * resolved through `getPlanForOrg` (ADR-0043) — a plan-tier entitlement enforced
 * at the CloudFront edge, which is a different mechanism that shares a word.
 */
export type PremiumPricingMethodology = 'monthly' | 'usage' | 'included';

/**
 * ENG-7032: a billable meter a premium integration emits. The CODE declares
 * WHAT is metered and in WHICH physical unit; the priced rate card
 * (integration_rate_cards) holds the per-unit price for each (event_type,
 * currency). `event_type` must match the value the integration's broker writes
 * to `integration_usage_events.event_type` (e.g. Deck's 'run_task'), so usage
 * rows can be joined to a rate.
 */
export interface PremiumMeter {
  /** The metered event the broker emits, e.g. 'run_task'. */
  event_type: string;
  /**
   * The physical unit one metered unit represents, e.g. 'run', 'character'.
   * This is a MACHINE key, not display copy: the admin pricing route writes it
   * verbatim into `integration_rate_cards.unit_type`, so keep it a short
   * snake_case token. Customer-facing prose belongs in `unit_label`.
   */
  unit: string;
  /**
   * Optional customer-facing display name for this meter in the pricing preview.
   * When omitted the UI humanizes `event_type` (e.g. 'run_task' -> 'Run Task').
   * Set it when the humanized name is unclear or when several meters need to read
   * as related fees, e.g. Deck's 'Base run fee' + 'Compute time'.
   */
  label?: string;
  /**
   * Optional customer-facing rendering of `unit`, used for the "(per X)" clause in
   * the pricing preview. Omit it for the self-explanatory units ('page', 'minute',
   * 'post') - the UI then shows `unit` as-is.
   *
   * Set it when the machine unit would leak jargon at a customer. The Apify-backed
   * integrations are the motivating case (ADR-0053): they meter a cost
   * pass-through, so their unit is a unit of MONEY rather than a thing the
   * customer asked for, and "(per usd)" tells them nothing. Display-only - it
   * never reaches the rate card.
   */
  unit_label?: string;
}

export interface PremiumDescriptor {
  /** Pricing methodology for this premium integration. */
  pricing: PremiumPricingMethodology;
  /**
   * Optional human-readable pricing note for the UI, e.g.
   * "Billed on Deck compute time and agent runs." Not a machine price.
   */
  note?: string;
  /**
   * ENG-7032: the billable meters this integration emits (usage-priced
   * integrations). Each declares an `event_type` + physical `unit`; the rate
   * card prices them per currency. Empty/absent for a monthly-priced premium
   * that meters nothing per operation.
   */
  meters?: PremiumMeter[];
  /**
   * ENG-7907: the flat monthly price for a `pricing: 'monthly'` premium, in
   * minor units (cents) per currency. Monthly premiums meter nothing per call,
   * so they have no `integration_rate_cards` rows - the amount lives here (a
   * flat platform-set fee is not org-specific), and it is the single source the
   * acknowledgement modal renders and the deferred Stripe billing slice reads.
   * Absent for usage-priced premiums (their amounts live in the rate card).
   */
  monthlyPrice?: { usdPriceMinor: number; audPriceMinor: number };
}

/**
 * ENG-10200: one voice modality an integration can do.
 *
 * WHAT A DECLARED MODALITY MEANS: the VENDOR offers it and this tree holds a
 * client for it. It does NOT mean a first-party route exposes it. Those are
 * different questions and conflating them makes the catalog wrong in a moving
 * way — ElevenLabs STT and TTS are reachable today only as broker AGENT tools
 * (`scribe-broker.ts`), so a reachability-based descriptor would have
 * ElevenLabs declare nothing until ENG-10201 ships a route, and would then need
 * editing again for every route after that. Reachability is the route slice's
 * concern; this is the catalog's.
 */
export interface VoiceModality {
  /**
   * The `premium.meters[].event_type` this modality bills under.
   *
   * A REFERENCE, not a copy. The physical `unit` stays single-sourced in
   * `premium.meters` precisely because the units differ per provider and would
   * rot if written twice: the two STT providers meter in `audio_hour`
   * (grok-voice) and `audio_second` (elevenlabs) respectively, and
   * `integration_usage_events` is append-only, so a wrong unit needs a credit
   * note rather than an UPDATE.
   */
  meterEventType: string;
}

/** A realtime (two-way streaming) voice modality. */
export interface VoiceRealtimeModality extends VoiceModality {
  /**
   * The provider exposes a voice roster we can enumerate at runtime.
   *
   * ENG-8272 built that for Grok (`packages/core/src/voice/grok-voices.ts`:
   * live fetch, three-rung degrade, union-not-intersection merge). This is a
   * CAPABILITY BIT, deliberately not an inlined list — a roster copied into the
   * catalog is a roster that cannot follow the vendor, which is the whole
   * defect ENG-8272 exists to fix.
   */
  listVoices: boolean;
}

/**
 * ENG-10200: what VOICE an integration can do, as three INDEPENDENT slots.
 *
 * Independent rather than a `mode` enum, because no provider we have offers
 * all three and each is missing a different one:
 *
 *   grok-voice   realtime + stt,  and NO tts (xAI ships no REST TTS endpoint;
 *                `api.x.ai/v1` is used here for /stt, /realtime/client_secrets
 *                and /videos only)
 *   elevenlabs   stt + tts,       and no realtime
 *
 * A single `stt_tts` mode would therefore report BOTH providers as capable of
 * a round trip neither can complete alone, and the caller would only find out
 * at the point of speaking.
 *
 * The realtime MODEL is deliberately absent. It is pinned once in
 * `GROK_VOICE_DEFAULT_MODEL` (`types/channel-config.ts`) and there is exactly
 * one realtime provider, so carrying it here would create a second source for
 * a pinned value with no reader. Move it in when a second realtime provider
 * makes "which model does this one use" a question worth asking.
 */
export interface VoiceDescriptor {
  realtime?: VoiceRealtimeModality;
  stt?: VoiceModality;
  tts?: VoiceModality;
}

/**
 * ENG-7015: picker-facing metadata for a customer-installable NATIVE
 * integration. When an `IntegrationDefinition` carries this, the Add Integration
 * picker offers it directly (not via the Composio DB catalog) and the org
 * allowlist must therefore govern it. It is the SINGLE source of truth that
 * replaces the two hand-aligned native lists (`STATIC_INTEGRATION_OPTIONS` in
 * the webapp + `NATIVE_PICKER_INTEGRATIONS` in the API), which used to drift.
 *
 * It carries the picker-facing display fields rather than reusing the
 * definition's own `category` / `supported_auth_types`, because those serve a
 * different purpose:
 *   - `category` here is the DISPLAY label the dialog groups by (e.g. "Code"),
 *     not the `IntegrationCategory` slug ("code") the runtime uses.
 *   - `authTypes` here is the set of auth options the CONNECT UI offers, which
 *     can differ from `supported_auth_types` (the runtime capability set) — e.g.
 *     xurl offers a keyless "none" option in the picker that the runtime spec
 *     omits, and GitHub's picker order is OAuth-first.
 */
export interface InstallablePickerMeta {
  /** Display category label shown in the Add Integration picker + org allowlist. */
  category: string;
  /** Auth options the connect UI offers (may differ from supported_auth_types). */
  authTypes: IntegrationAuthType[];
}

export interface IntegrationDefinition {
  id: IntegrationId;
  name: string;
  category: IntegrationCategory;
  description: string;
  supported_auth_types: IntegrationAuthType[];
  capabilities: IntegrationCapability[];
  config_schema?: object;
  icon?: string;
  docs_url?: string;
  cli_tool?: IntegrationCliTool;
  /**
   * ENG-7015: present when this integration is a customer-installable native
   * that the Add Integration picker offers directly. Carries the picker-facing
   * display metadata (see {@link InstallablePickerMeta}). Both the picker and
   * the org allowlist derive their curated native list from the entries that
   * set this, so there is exactly one source for "which natives are
   * installable". Absent for Composio-catalog integrations (DB-driven), runtime
   * frameworks (claude-code, v0), dev CLIs (coderabbit) and internal staff-only
   * tools (augmented-admin) — none of which are customer-installable.
   */
  installable?: InstallablePickerMeta;
  /**
   * ENG-8804: this integration is PLATFORM-INTERNAL — attached by Augmented Team
   * at provisioning or by central policy, never by a human completing a connect
   * flow. An agent must not be offered it, and must not be able to request it.
   *
   * It exists because nothing else in this type expresses that, and the two
   * fields that look like they might are both wrong in the expensive direction:
   *
   *   - Absence of `installable` does NOT mean internal. That descriptor governs
   *     the NATIVE Add-Integration picker only; firecrawl, x-search, ayrshare
   *     and the Direct-HTTP seeds all lack it and are real customer integrations
   *     an agent should be able to ask for.
   *   - `supported_auth_types: ['none']` does NOT mean internal either. deck,
   *     browserbase, elevenlabs, grok-voice, image-gen, video-gen, qmd,
   *     coderabbit, ayrshare and x-search are all auth-`none` managed
   *     integrations that agents SHOULD see.
   *
   * Inferring from either would quietly stop agents asking for a dozen real
   * integrations, and that symptom — agents that no longer request things they
   * need — is invisible from every surface we have. Hence an explicit flag.
   *
   * This is a VISIBILITY marker, not an authorization boundary: the org-lock on
   * `augmented-support` / `augmented-admin` is enforced server-side from the
   * host JWT (ADR-0031/0032) and remains the thing that actually protects them.
   */
  platform_internal?: boolean;
  /** Marks the integration as experimental — UI shows a "Beta" badge */
  beta?: boolean;
  /**
   * ENG-6920: marks an integration as PREMIUM (billable). Absent => free: it
   * authenticates with the customer's own account (Linear, GitHub, the
   * Composio/OAuth set), so upstream cost lands on the customer's bill and
   * there is nothing for Augmented to meter. A premium integration is one
   * Augmented pays for centrally (e.g. Deck's single account key), so it is
   * gated on a per-org opt-in entitlement and its usage is metered.
   *
   * This descriptor is the catalog foundation only — the entitlement model,
   * the enable-gate, usage metering and budget caps are separate slices that
   * READ it. It carries no machine price; actual amounts live with the billing
   * mechanism (Stripe), which is deferred.
   */
  premium?: PremiumDescriptor;
  /**
   * ENG-10200: which voice modalities this integration can do. Absent for the
   * overwhelming majority — it is set only on providers that actually speak or
   * listen. See {@link VoiceDescriptor} for what a declared modality asserts
   * (vendor capability, NOT first-party-route reachability).
   */
  voice?: VoiceDescriptor;
  /**
   * ENG-5815: data-driven native (stdio) MCP server entry. When set,
   * the claudecode framework adapter emits this entry into `.mcp.json`
   * via the templated renderer in `provisioning/native-mcp.ts`, no
   * core code change required to add a new integration.
   *
   * Integrations with conditional rendering (broker-mode toggles,
   * config-derived env, etc.) still need a hand-rolled handler — leave
   * `nativeMcp` undefined for those and keep the if-block. See
   * `claudecode/index.ts:buildMcpJson` for the migration boundary.
   */
  nativeMcp?: import('../provisioning/native-mcp.js').NativeMcpSpec;
  /**
   * ENG-5855: data-driven HOSTED remote (streamable-HTTP / SSE) MCP server
   * entry with custom, non-OAuth header auth. When set, the claudecode
   * adapter emits this entry into `.mcp.json` via `buildRemoteMcpEntry`,
   * no core code change required to add a new integration.
   *
   * This is the api-key-header sibling of the OAuth `mcpUrl` path: instead
   * of a fixed `Authorization: Bearer ${ID_ACCESS_TOKEN}` header, the spec
   * carries an arbitrary templated headers map (e.g. Anchor Browser's
   * `anchor-api-key` + dynamic `anchor-session-id`). Leave undefined for
   * OAuth remote MCPs — those stay on the `OAUTH_PROVIDERS.mcpUrl` path.
   */
  remoteMcp?: RemoteMcpSpec;
}

export interface IntegrationCredentials {
  api_key?: string;
  access_token?: string;
  refresh_token?: string;
  /** ISO timestamp when the access_token expires */
  token_expires_at?: string;
  [key: string]: unknown;
}

export interface Integration {
  id: string;
  scope: IntegrationScope;

  organization_id?: string;
  team_id?: string;
  agent_id?: string;

  definition_id: string;
  display_name: string;

  auth_type: IntegrationAuthType;
  credentials: IntegrationCredentials;
  config: Record<string, unknown>;

  /**
   * ENG-8304 / ADR-0055: who owns the credential (managed | byo). Optional for
   * back-compat with callers/tests that predate the column; absent is read as
   * 'managed' (the storage default), which is also the only value that existed
   * before this axis. Load it explicitly wherever the derived-billing predicate
   * (`isBilledInstall`) or a BYO-aware branch needs it.
   */
  credential_source?: CredentialSource;

  status: IntegrationStatus;
  status_message?: string;

  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface ResolvedIntegration {
  /**
   * ENG-4920: integration row UUID. Surfaced through to provisioning so
   * vendor MCP servers (xero-mcp-server, …) can be wired with
   * `AGT_INTEGRATION_ID` and call the per-call credential broker
   * endpoint (POST /host/agent-integrations/:id/credential) instead of
   * baking the access_token into spawn-time env. Optional for
   * back-compat with code paths that build a ResolvedIntegration
   * without going through /host/agent-integrations (e.g. tests).
   */
  id?: string;
  definition_id: string;
  /**
   * ENG-8359 (ADR-0045 / ENG-7543): which connection of this definition the row
   * is — `'default'` (or absent) for the single-connection fleet, a slug for a
   * named one. `/host/agent-integrations` has forwarded it since ENG-7543 and
   * the manager's mapping preserves it, but it was never declared here, so the
   * provisioning writer could not see it: every remote-MCP `.mcp.json` entry and
   * every credential env var was keyed by `definition_id` alone, and a second
   * connection silently overwrote the first on disk.
   *
   * Optional: callers that build a `ResolvedIntegration` by hand (tests, older
   * paths) omit it and get the default-connection naming, unchanged.
   */
  connection_key?: string | null;
  display_name: string;
  scope: IntegrationScope;
  auth_type: IntegrationAuthType;
  credentials: IntegrationCredentials;
  config: Record<string, unknown>;
  capabilities: IntegrationCapability[];
  /**
   * ENG-6993 / ADR-0033 (Slice 2): the hosted-remote-MCP descriptor sourced
   * from the integration's `integration_definitions.remote_mcp` catalog column.
   * When present, provisioning renders the `.mcp.json` entry from THIS spec
   * (the DB catalog row is the source of truth) instead of looking the spec up
   * in the code `INTEGRATION_REGISTRY`. Absent for non-remote-MCP integrations,
   * and for callers not yet wired to forward the column — those fall back to the
   * code registry, so the output is unchanged (Anchor stays byte-identical).
   * Retires the code/DB duality the ADR calls out.
   */
  remoteMcp?: RemoteMcpSpec;
  /**
   * ENG-10614: the per-tool HITL floors for this remote MCP, from
   * `tool_definitions.min_hitl_tier` keyed on `(toolkit_id, tool_key)`.
   *
   * Forwarded so the host-side proxy can enforce them. Before this, the column
   * was read by nothing on the remote-MCP lane: a hosted API-key MCP is called
   * by Claude Code straight at the vendor, so the catalog said `delete_item`
   * needed approval and the call went out anyway.
   *
   * ABSENT AND EMPTY MEAN DIFFERENT THINGS and both are load-bearing. Absent is
   * "this caller does not forward tiers" (older paths, tests) and leaves the
   * lane ungated exactly as before. An empty object would claim "catalogued, and
   * nothing gates", which the proxy would read as every tool being unknown.
   * Omit rather than default to `{}`.
   */
  toolTiers?: Record<string, HitlTier>;
  /**
   * ENG-7358: catalog cutover flag for integrations served by a DEDICATED
   * LOCAL stdio MCP server bundled with the CLI (`~/.augmented/_mcp/<id>.js`).
   * Sourced from `integration_definitions.metadata.stdio_mcp` and forwarded
   * through `/host/agent-integrations` (snake_case `stdio_mcp`, mapped by the
   * manager alongside `remote_mcp` -> `remoteMcp`). When true, the claudecode
   * adapter emits the integration's own `.mcp.json` server entry; when
   * false/absent nothing changes, so the code path ships dark and cutover +
   * rollback are pure catalog operations. Origami is the first user.
   */
  stdioMcp?: boolean;
  /**
   * ENG-8344: how this integration's credential reaches the agent.
   *
   *  - `'env'` / absent — historical behaviour: the payload carries the token
   *    and the adapter materializes it into `.env.integrations`.
   *  - `'broker'` — the payload carries NO token. The adapter installs the
   *    use-time fetchers (git credential helper + `gh` shim) instead, so a
   *    rotating credential never enters the agent's process environment and
   *    never forces a session respawn to be delivered.
   *
   * Set API-side by `/host/agent-integrations` for `auth_type='github_app'`
   * rows when the `github-broker-credentials` flag is on, and forwarded across
   * the manager seam as snake_case `credential_delivery`. Absent for every
   * other row and for hosts/tests that predate the field, which read as `'env'`.
   */
  credentialDelivery?: 'env' | 'broker';
}

// ---------------------------------------------------------------------------
// ENG-7168: folded in from the former types/plugin.ts (integration-domain
// types kept under the legacy 'plugin' filename during the ENG-6009 rename).
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// IntegrationDef Skills
// ---------------------------------------------------------------------------

export interface IntegrationDefSkill {
  id: string;
  name: string;
  content: string;
  references: Array<{ url: string; label?: string }>;
  /** Which scopes this skill covers. undefined/empty = all scopes (legacy). */
  scope_ids?: string[];
}

// ---------------------------------------------------------------------------
// IntegrationDef Scripts / Hooks
// ---------------------------------------------------------------------------

export interface IntegrationDefScripts {
  on_install?: string;
  on_uninstall?: string;
  on_upgrade?: string;
  on_connect?: string;
}

// ---------------------------------------------------------------------------
// IntegrationDef Permission Scopes
// ---------------------------------------------------------------------------

/**
 * Canonical HITL tier order (ENG-5123 / ADR 0004). Strictness increases
 * left-to-right: read < write < write_high_risk < write_destructive < admin.
 * The HITL resolver maps tier strings to this index and picks the highest.
 *
 * NOTE: `@augmented/approval-core` declares the same union as
 * `ApprovalRiskTier`. Duplicated here (with shared values) to avoid a
 * core→approval-core dependency cycle. Unifying behind a shared package is a
 * follow-up; both unions must stay in lockstep until then.
 */
export const HITL_TIER_ORDER = [
  'read',
  'write',
  'write_high_risk',
  'write_destructive',
  'admin',
] as const;

export type HitlTier = (typeof HITL_TIER_ORDER)[number];

/** Canonical ordinal — higher = stricter. Use for comparisons, never lexical. */
export const HITL_TIER_RANK: Readonly<Record<HitlTier, number>> = Object.freeze(
  Object.fromEntries(HITL_TIER_ORDER.map((tier, i) => [tier, i])) as Record<
    HitlTier,
    number
  >,
);

/**
 * Install-time / definition-time per-tool override, stored on
 * `agent_integrations.tool_overrides` and on
 * `integration_definitions.defined_scopes[].tool_overrides`.
 *
 * Two arms, discriminated by the presence of `verdict`:
 *
 *  - **raise** (`ToolHitlRaise`) — lifts the required approval tier above
 *    the catalog floor. Always RAISES, never lowers; validators reject any
 *    `raised_to` whose rank is less-than-or-equal to the floor for
 *    `tool_key`.
 *  - **deny** (`ToolHitlDeny`, ENG-10256 / ADR-0080 decision 5) — a
 *    TERMINAL refusal of this one tool. The call never runs and no
 *    approval is offered.
 *
 * `verdict` is absent on every row written before ENG-10256, so existing
 * JSONB rows parse as `ToolHitlRaise` unchanged and no migration is
 * required (the column is plain `jsonb` with no CHECK constraint).
 *
 * **Deny is deliberately not a tier.** Two independent reasons, and the
 * second is a live fail-open rather than a modelling preference:
 *
 *  1. Rank cannot express it. `HITL_TIER_RANK` orders five tiers and a
 *     terminal refusal has no rank, so it cannot be compared, raised past,
 *     or validated against a floor.
 *  2. A `'deny'` string in a tier slot FAILS OPEN. The CLI lane gates on
 *     `APPROVAL_TIERS.has(tier)`; a value outside that set returns false
 *     and the tool EXECUTES — by exactly the mechanism the deny exists to
 *     prevent. Keeping the verdict in its own field, and returning it from
 *     the resolver as a discriminated union, makes every consumer narrow
 *     before it can read `.tier` at all.
 *
 * It is also not the same control as `hitl-approval-routing: off`, which
 * dead-ends *every* blocked tool in the org at once. This is per tool, at
 * agent grain: "never delete a Dropbox file, but still ask before sending
 * an email, and keep deleting calendar events" (CS-1647).
 */
export interface ToolHitlRaise {
  /** Provider-native tool identifier — must match `tool_definitions.tool_key`. */
  tool_key: string;
  /** New ceiling for this tool. MUST raise per HITL_TIER_RANK. */
  raised_to: HitlTier;
  /** Required prose explaining why this override exists. */
  justification: string;
  /** Absent on this arm — present only on `ToolHitlDeny`. */
  verdict?: undefined;
}

export interface ToolHitlDeny {
  /** Provider-native tool identifier — must match `tool_definitions.tool_key`. */
  tool_key: string;
  /** Terminal refusal. The call never runs and no approval is offered. */
  verdict: 'deny';
  /** Required prose explaining why this tool is denied. */
  justification: string;
  /** Absent on this arm — a deny has no tier, by construction. */
  raised_to?: undefined;
}

export type ToolHitlOverride = ToolHitlRaise | ToolHitlDeny;

/**
 * Narrow a `ToolHitlOverride` to its deny arm.
 *
 * Prefer this over a bare `o.verdict === 'deny'` at call sites that also
 * read `raised_to`: it narrows BOTH fields, so the compiler stops a reader
 * from treating a denied tool as though it carried a tier.
 */
export function isToolHitlDeny(o: ToolHitlOverride): o is ToolHitlDeny {
  return o.verdict === 'deny';
}

/**
 * Per-install approver routing override stored on
 * `agent_integrations.approver_route`. Discriminated by `kind`:
 *  - `channel`: route to a shared channel from the channel registry. The
 *    channel must be installed for the team and at an appropriate security
 *    tier for the strictest verb tier (validated server-side, fail-closed).
 *  - `dm`: route to a specific user via their preferred contact channel.
 *    The user MUST be a member of the same team as the agent_integrations
 *    row AND eligible to approve at the strictest tier in the install.
 *    Channel preference is resolved against `user_channel_preferences`.
 */
export type ApproverRoute =
  | { kind: 'channel'; channel_type: string; channel_id: string }
  | { kind: 'dm'; user_id: string };

/**
 * Output of the Integration Mode interview (ENG-5129). Consumed by the
 * manifest validator and ultimately persisted as an
 * `integration_definitions` row at `scope=team, status=draft`.
 *
 * The shape mirrors `integration_definitions.defined_scopes` so the
 * persistence path is a near-straight copy. The skill (`.claude/skills/
 * integration-mode/SKILL.md`) is the affordance that gathers these
 * fields; this type is the contract the API validates.
 */
export interface IntegrationManifest {
  /** Free-text outcome the contributor articulated in step 1. */
  goal: string;
  /** Optional pre-binding to a specific agent. */
  target_agent_id?: string;
  /** Each toolkit + its enabled scopes. Multi-toolkit is allowed; composition is deferred. */
  toolkits: Array<{
    /** Matches `toolkit_definitions.id`. */
    toolkit_id: string;
    scopes: IntegrationManifestScope[];
  }>;
  /**
   * sha256 hex of the canonicalised manifest body (goal + toolkits).
   * Lets the API confirm the contributor saw the exact replay they
   * confirmed at step 5 — prevents drift between interview and persist.
   */
  confirmation_hash: string;
}

export interface IntegrationManifestScope {
  /** Unique within this manifest; conventionally `<toolkit>:<verb>`. */
  scope_id: string;
  name: string;
  description: string;
  default_min_role: TeamRole;
  /** Provider tool_keys; each MUST exist in `tool_definitions`. */
  tools: string[];
  /** RAISE-only per-tool overrides; justification required. */
  tool_overrides: ToolHitlOverride[];
}

export interface IntegrationDefScope {
  /** Unique scope identifier, e.g. 'xero:invoices:read' */
  id: string;
  /** Human-readable name, e.g. 'Read Invoices' */
  name: string;
  description: string;
  /** Which toolkit this scope belongs to */
  toolkit_id: string;
  /** Provider action slugs this scope grants access to */
  tools: string[];
  /**
   * Optional per-tool HITL raises declared at the integration_definition
   * level. Each entry strictly raises the corresponding tool's catalog floor
   * for any agent installing this integration. Validators enforce the
   * strict-raise invariant against `tool_definitions.min_hitl_tier`.
   */
  tool_overrides?: ToolHitlOverride[];
  /** IntegrationDef author's recommended minimum role to grant this scope */
  default_min_role: TeamRole;
  /**
   * OAuth provider scope strings this catalog scope requires at consent
   * time, e.g. `['payroll.employees', 'payroll.payruns']` for `xero:payroll`.
   * Used by the agent-scope-deficit calculator to detect when an installed
   * skill needs OAuth scopes the existing credential doesn't carry — refresh
   * tokens can never widen scope, so the only remediation is reconnect.
   * Omitted/empty means the scope has no OAuth-side dependency.
   */
  oauth_scopes?: string[];
}

// ---------------------------------------------------------------------------
// IntegrationDef
// ---------------------------------------------------------------------------

// ENG-7063: `alpha` sits earlier than `beta` in the maturity lifecycle. Alpha
// integrations are fully installable + usable but HIDDEN from end users by
// default — they surface only to Integrity-Labs admins who opt in via a
// "Show Alpha" toggle (admin route `?include_alpha=true` + the dialog checkbox;
// that reveal UI is the follow-up slice). Visibility matrix: non-admins see only
// `published`; admins see `beta` by default and `alpha`/`draft` behind opt-in flags.
export type IntegrationDefStatus = 'draft' | 'alpha' | 'beta' | 'published' | 'archived';

export interface IntegrationDef {
  id: string;
  organization_id: string | null;
  team_id: string | null;
  name: string;
  slug: string;
  description: string | null;
  category: string;
  icon: string | null;
  required_toolkits: string[];
  skills: IntegrationDefSkill[];
  allowed_tools: string[];
  scripts: IntegrationDefScripts;
  defined_scopes: IntegrationDefScope[];
  /**
   * Optional JSON Schema (Draft 2020-12, constrained subset) declaring the
   * typed context fields this plugin accepts. NULL means the plugin has no
   * typed context — only the universal freeform overrides field is available.
   * See ENG-4341 / docs/plugins/plugin-context-rfc.md.
   */
  context_schema: IntegrationContextSchema | null;
  version: number;
  published_at: string | null;
  status: IntegrationDefStatus;
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// IntegrationDef Context (ENG-4341)
//
// User-supplied per-plugin tuning data. Two parts:
//   - `values`:    typed config validated against `IntegrationDef.context_schema`
//   - `overrides`: freeform Markdown appended verbatim to every rendered
//                  SKILL.md as a "## Team Overrides" section
// ---------------------------------------------------------------------------

/**
 * The constrained subset of JSON Schema (Draft 2020-12) that plugin authors
 * may declare for their context. The full JSON Schema spec is much larger;
 * we only support what `@vercel-labs/json-render` can render and Ajv can
 * validate without escape hatches.
 *
 * Supported field types:
 *   - string  (with optional `enum`)
 *   - boolean
 *   - array of string
 *   - flat object as additionalProperties: { type: string } (key-value map)
 *
 * Deferred (will reject in meta-schema validation):
 *   - number / integer
 *   - nested object schemas
 *   - oneOf / anyOf / $ref / format (beyond Ajv defaults)
 *   - the x-augmented-dimension extension keyword (cut from slice 1; see RFC §1b)
 */
export interface IntegrationContextSchema {
  $schema?: string;
  type: 'object';
  properties: Record<string, IntegrationContextFieldSchema>;
  required?: string[];
}

export type IntegrationContextFieldSchema =
  | IntegrationContextStringField
  | IntegrationContextBooleanField
  | IntegrationContextStringArrayField
  | IntegrationContextStringMapField;

interface IntegrationContextFieldBase {
  title?: string;
  description?: string;
}

export interface IntegrationContextStringField extends IntegrationContextFieldBase {
  type: 'string';
  enum?: string[];
  default?: string;
}

export interface IntegrationContextBooleanField extends IntegrationContextFieldBase {
  type: 'boolean';
  default?: boolean;
}

export interface IntegrationContextStringArrayField extends IntegrationContextFieldBase {
  type: 'array';
  items: { type: 'string' };
  default?: string[];
}

export interface IntegrationContextStringMapField extends IntegrationContextFieldBase {
  type: 'object';
  additionalProperties: { type: 'string' };
  default?: Record<string, string>;
}

/**
 * Concrete value types that can be stored in IntegrationContext.values, derived
 * from the schema field types above. Top-level keys correspond to property
 * names in the plugin's `context_schema.properties`.
 */
export type IntegrationContextValue =
  | string
  | boolean
  | string[]
  | Record<string, string>;

export type IntegrationContextValues = Record<string, IntegrationContextValue>;

export type IntegrationContextScope = 'organization' | 'team' | 'agent';

export interface IntegrationContext {
  id: string;
  plugin_id: string;
  scope: IntegrationContextScope;
  organization_id: string | null;
  team_id: string | null;
  agent_id: string | null;
  values: IntegrationContextValues;
  /** Freeform Markdown — never validated against context_schema. */
  overrides: string;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Pre-resolved plugin context delivered via /host/refresh. Inheritance
 * (org → team → agent) and schema defaults have already been flattened
 * server-side. The manager just consumes this and substitutes.
 */
export interface ResolvedIntegrationContext {
  plugin_id: string;
  plugin_slug: string;
  values: IntegrationContextValues;
  /**
   * Resolved freeform overrides text. When multiple scopes have non-empty
   * overrides, they are concatenated under `### Organization-wide`,
   * `### Team`, and `### Agent-specific` sub-headings.
   */
  overrides: string;
}

/** Append-only audit row for changes to IntegrationContext.overrides. */
export interface IntegrationContextOverridesAuditEntry {
  id: string;
  agent_integration_context_id: string;
  changed_by: string | null;
  changed_at: string;
  before_value: string | null;
  after_value: string;
}

// ---------------------------------------------------------------------------
// Agent ↔ IntegrationDef binding
// ---------------------------------------------------------------------------

export interface AgentIntegrationInstall {
  id: string;
  agent_id: string;
  plugin_id: string;
  plugin_version: number;
  auto_upgrade: boolean;
  /** Subset of plugin.defined_scopes[].id. Empty array = all scopes (legacy/backward compat). */
  granted_scopes: string[];
  /** Skill IDs the user opted out of. Empty array = no exclusions, all skills deployed. */
  excluded_skill_ids: string[];
  installed_at: string;
  installed_by: string | null;
  upgraded_at: string | null;
}

// ---------------------------------------------------------------------------
// Team-level scope overrides
// ---------------------------------------------------------------------------

export interface IntegrationScopeOverride {
  id: string;
  team_id: string;
  plugin_id: string;
  /** References plugin.defined_scopes[].id */
  scope_id: string;
  /** Overridden minimum role required to grant this scope */
  min_role: TeamRole;
  created_by: string;
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// Scope approval requests
// ---------------------------------------------------------------------------

export type ScopeRequestStatus = 'pending' | 'approved' | 'denied' | 'expired';

export interface IntegrationScopeRequest {
  id: string;
  team_id: string;
  agent_id: string;
  plugin_id: string;
  /** Scope IDs being requested */
  requested_scopes: string[];
  reason: string | null;
  status: ScopeRequestStatus;
  requested_by: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  expires_at: string | null;
  created_at: string;
}
